import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import FigoLottie from '../../../common/FigoLottie';
import { CurrentPaymentMethodCard } from '../CurrentPaymentMethodCard';

import {
  useOnPostMessage,
  usePersonalInformation,
  usePaymentManagement,
  useMixpanel,
} from '../../../../hooks';
import {
  MESSAGE_TYPE,
  PAYMENT_TYPE,
} from './paymentech.constants';
import { fetchOrcaProxy, getData } from '../../../../services/api';
import { WEBVIEW_API_TOKEN } from '../../../../services/constants';
import {
  IFRAME_LOAD_ERROR,
  IFRAME_LOAD_TIMEOUT_MS,
  PAYMENT_PROVIDER_NAMES,
  PAYMENT_PROVIDERS_ID,
} from '../billingAndPayments.constants';
import { EV60, EV61, EV62, EV63, EV64 } from '../../../../services/mixpanel';
import { getBrandNameByChannelId } from '../../../../services/utils';
import { ENTRY_POINT, PAYMENT_ERROR_RESULT } from '../../../../services/mixpanel.constants';
import {
  loadBillingInfoAllBrand,
  updatePaymentMethodCa,
  updatePaymentMethodCaReset,
} from '../../../../actions';

import styles from './PaymentechIframe.module.css';

const PaymentechIframe = ({
  entryPoint = ENTRY_POINT.myAccount,
  marketingChannelId = -1,
  paymentMethod = '',
  paymentType = '',
  policyIdFigo = '',
}) => {
  const iframeRef = useRef();
  const iframeLoadTimeoutRef = useRef(null);
  const [formUid, setFormUid] = useState(null);
  const [loadingFormUid, setLoadingFormUid] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);
  const updateInProgressRef = useRef(false);
  const { userInformation } = usePersonalInformation();
  const paymentManagement = usePaymentManagement({ policyIdFigo });
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const { sendEvent } = useMixpanel();

  const doLogSubmitPMEvent = useCallback(({ error = '' }) => {
    sendEvent(EV62({
      brandName: getBrandNameByChannelId(marketingChannelId),
      entryPoint,
      errorCode: error,
      marketChannelId: marketingChannelId,
      paymentProvider: PAYMENT_PROVIDER_NAMES.PAYMENTECH,
      result: error ? PAYMENT_ERROR_RESULT.error : PAYMENT_ERROR_RESULT.success,
    }));
  }, [marketingChannelId, entryPoint, sendEvent]);

  const savePaymentMethod = useCallback(async () => {
    try {
      const payload = {
        active: true,
        address1: userInformation.address.line1,
        address2: userInformation.address.line2,
        city: userInformation.address.city,
        customerId: paymentManagement.customerId,
        firstName: userInformation.firstName,
        gatewayToken: formUid,
        lastName: userInformation.lastName,
        paymentProviderId: PAYMENT_PROVIDERS_ID.PAYMENTECH,
        // fixed for all paymentech users
        paymentTypeId: PAYMENT_TYPE.CREDIT_CARD,
        postalCode: userInformation.address.zipCode,
      };

      const proxyToken = await getData(WEBVIEW_API_TOKEN);

      const path = 'orca-proxy/billing/v1/payment-option';
      const response = await fetchOrcaProxy(
        `${path}?base-url=${paymentManagement.paymentManagementUrl}`,
        {
          body: JSON.stringify(payload),
          headers: {
            Authorization: `Bearer ${proxyToken}`,
            'Content-Type': 'application/json',
          },
          method: 'POST',
        },
      );

      if (response.ok) {
        // success scenario will be only logged once the whole flow
        // is completed successfuly (orca and figo endpoints)
        // at this point only Orca returned a succesfuly response
        dispatch(updatePaymentMethodCa({
          customerId: paymentManagement.customerId,
          paymentMethodId: formUid,
          paymentProviderId: PAYMENT_PROVIDERS_ID.PAYMENTECH,
          policyId: paymentManagement.policyId,
        }));

        setFormUid(null);
        return;
      }

      const responsePayload = await response.json();
      const errorMessage = responsePayload.error || responsePayload.message
          || JSON.stringify(responsePayload);

      setErrorMessages([{ status_message: errorMessage }]);

      // orca endpoint error logging
      doLogSubmitPMEvent({ error: errorMessage });
    } catch (ex) {
      // eslint-disable-next-line no-console
      console.error('error', ex);

      // orca endpoint error logging
      doLogSubmitPMEvent({
        error: ex.message || ex.error || JSON.stringify(ex),
      });
    }
  }, [
    dispatch,
    doLogSubmitPMEvent,
    formUid,
    paymentManagement,
    process.env,
    userInformation,
  ]);

  useEffect(() => {
    if (paymentManagement.updated) {
      // Figo endpoint submit logging
      doLogSubmitPMEvent({ error: paymentManagement.paymentMethodError });

      // user finishes the flow, doesn't matter if the endpoint response
      // is successful or failure
      updateInProgressRef.current = false;

      // finally resets the whole update PM redux state
      dispatch(updatePaymentMethodCaReset());
    }
  }, [paymentManagement.updated, marketingChannelId, doLogSubmitPMEvent]);

  const clearIframeLoadTimeout = useCallback(() => {
    clearTimeout(iframeLoadTimeoutRef.current);
  }, []);

  const logIframeLoadError = useCallback((errorCode) => {
    sendEvent(EV64({
      brandName: getBrandNameByChannelId(marketingChannelId),
      entryPoint,
      errorCode,
      marketChannelId: marketingChannelId,
      paymentProvider: PAYMENT_PROVIDER_NAMES.PAYMENTECH,
    }));
  }, [entryPoint, marketingChannelId, sendEvent]);

  const onIframeLoadTimeout = useCallback(() => {
    logIframeLoadError(IFRAME_LOAD_ERROR);
  }, [logIframeLoadError]);

  useEffect(() => {
    if (!formUid) {
      return undefined;
    }

    // iframe onError doesn't fire on failed loads
    // and onLoad fires for both success and failure loading
    // so we calculate the error based in a timeout
    iframeLoadTimeoutRef.current = setTimeout(
      onIframeLoadTimeout,
      IFRAME_LOAD_TIMEOUT_MS,
    );

    return clearIframeLoadTimeout;
  }, [formUid]);

  const onMessage = useCallback(({ data, origin, source }) => {
    const messageType = data?.subject || '';
    let errors = null;

    // any message from the iframe origin means the form loaded successfully
    clearIframeLoadTimeout();

    switch (messageType) {
      case MESSAGE_TYPE.CLEAR_ERRORS:
        setErrorMessages([]);
        break;

      case MESSAGE_TYPE.HANDLE_TRANSACTION_ERRORS:
        // an array of objects
        errors = data.content;
        setErrorMessages(errors);
        break;

      case MESSAGE_TYPE.START_PAYMENT:
        // eslint-disable-next-line no-console
        console.info('payment started in paymentech form...');
        break;

      case MESSAGE_TYPE.COMPLETE_PAYMENT:
        savePaymentMethod();
        break;

      case MESSAGE_TYPE.INIT:
        if (data.reply) {
          const targetWindow = source;

          if (targetWindow) {
            const payload = {
              messageId: data.messageId,
              reply: true,
              subject: MESSAGE_TYPE.INIT,
            };

            targetWindow.postMessage(payload, origin);

            // eslint-disable-next-line no-console
            console.info('handshake completed', data.messageId);
          }
        }
        break;

      default:
        // eslint-disable-next-line no-console
        console.warn('UNKNOWN MESSAGE', data);
        break;
    }
  }, [clearIframeLoadTimeout, savePaymentMethod, iframeRef.current]);

  const formUrl = useMemo(() => `${process.env
    .REACT_APP_PAYMENTECH_IFRAME_URL}/?uid=${encodeURIComponent(formUid)}`,
  [formUid, process.env]);

  useOnPostMessage({ onMessage, url: formUrl });

  const fetchFormUid = useCallback(async () => {
    try {
      setFormUid(null);
      setLoadingFormUid(true);

      const language = i18n.language.split('-')[0];
      const payload = {
        language,
        paymentType: PAYMENT_TYPE.CREDIT_CARD,
      };

      const response = await fetch(
        `${paymentManagement.paymentManagementUrl
        }/billing/v1/paymentech/iframe`,
        {
          body: JSON.stringify(payload),
          headers: {
            'Content-Type': 'application/json',
          },
          method: 'POST',
        },
      );

      if (!response.ok) {
        throw new Error('Failed to fetch UID');
      }

      const data = await response.json();
      // Update the iframe src with the new URL
      setFormUid(data.uid);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching UID:', error);

      // without uid the iframe can't be loaded, so the flow ends here
      updateInProgressRef.current = false;
      logIframeLoadError(error.message);
    } finally {
      setLoadingFormUid(false);
    }
  }, [i18n.language, logIframeLoadError, paymentManagement]);

  const onEditClick = useCallback(() => {
    if (paymentManagement.paymentManagementUrl) {
      fetchFormUid();
      sendEvent(EV61({
        brandName: getBrandNameByChannelId(marketingChannelId),
        entryPoint,
        marketChannelId: marketingChannelId,
        paymentProvider: PAYMENT_PROVIDER_NAMES.PAYMENTECH,
      }));

      updateInProgressRef.current = true;
    }
  }, [fetchFormUid, paymentManagement, marketingChannelId]);

  useEffect(() => {
    dispatch(loadBillingInfoAllBrand());

    sendEvent(EV60({
      brandName: getBrandNameByChannelId(marketingChannelId),
      entryPoint,
      marketChannelId: marketingChannelId,
      paymentProvider: PAYMENT_PROVIDER_NAMES.PAYMENTECH,
    }));
  }, []);

  useEffect(() => () => {
    // on unmount, if process started but not concluded
    if (updateInProgressRef.current) {
      sendEvent(EV63({
        brandName: getBrandNameByChannelId(marketingChannelId),
        entryPoint,
        marketChannelId: marketingChannelId,
        paymentProvider: PAYMENT_PROVIDER_NAMES.PAYMENTECH,
      }));
    }
  }, []);

  return (
    <div className={styles.mainContainer}>
      {!formUid && !loadingFormUid && !paymentManagement.paymentMethodLoading
        && (
          <CurrentPaymentMethodCard
            onAddPaymentMethod={onEditClick}
            paymentMethod={paymentMethod}
            showEditButton
            type={paymentType}
          />
        )}

      {paymentManagement.paymentMethodLoading || loadingFormUid
        ? <FigoLottie /> : null}

      {formUid ? (
        <iframe
          ref={iframeRef}
          className={styles.iframe}
          src={formUrl}
          title="Paymentech"
        />
      ) : null}

      <div className={styles.column}>
        {errorMessages.map((error) => (
          <span className={styles.errorLabel}>
            {error.status_message}
          </span>
        ))}
      </div>
    </div>
  );
};

export { PaymentechIframe };
